import { useRef, useEffect } from 'react';
import AWS from 'aws-sdk';

const identityPoolId = process.env.REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID;

AWS.config.update({
    region: 'ap-south-1',
    credentials: new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId,
    }),
});

const polly = new AWS.Polly();

const useSpeechControl = (messages, isSpeaking, setIsSpeaking, speechEnabled, setSpeechEnabled) => {
    const speechRef = useRef(null);
    const lastPlayedMessageIndex = useRef(-1);
    // Play the specified message
    const playMessage = (text) => {
        // Stop existing speech if any
        if (speechRef.current) {
            speechRef.current.pause();
            speechRef.current.currentTime = 0;
        }

        if (!speechEnabled) return;

        // Get temporary credentials before calling Polly
        AWS.config.credentials.get((err) => {
            if (err) {
                console.error('Failed to get AWS credentials:', err);
                return;
            }

            const params = {
                OutputFormat: 'mp3',
                Text: text,
                VoiceId: 'Raveena',
                Engine: 'standard',
            };

            polly.synthesizeSpeech(params, (err, data) => {
                if (err) {
                    console.error('Polly error:', err);
                    setIsSpeaking(false);
                    return;
                }

                const audio = new Audio();
                audio.src = URL.createObjectURL(new Blob([data.AudioStream], { type: 'audio/mp3' }));

                audio.onplay = () => setIsSpeaking(true);
                audio.onended = () => setIsSpeaking(false);
                audio.onerror = () => setIsSpeaking(false);

                speechRef.current = audio;
                audio.play().catch(err => {
                    console.error('Error playing audio:', err);
                    setIsSpeaking(false);
                });
            });
        });
    };

    // Effect to monitor messages and play new ones
    useEffect(() => {
        if (messages.length === 0) return;

        const currentIndex = messages.length - 1;
        const currentMessage = messages[currentIndex];

        // Only play if it's a new message, it's from Sensei, and speech is enabled
        if (
            currentIndex > lastPlayedMessageIndex.current &&
            currentMessage.type === "Sensei" &&
            speechEnabled
        ) {
            lastPlayedMessageIndex.current = currentIndex;
            playMessage(currentMessage.text);
        }

        return () => {
            if (speechRef.current) {
                speechRef.current.pause();
                speechRef.current.currentTime = 0;
                setIsSpeaking(false);
            }
        };

    }, [messages, speechEnabled]);

    // Toggle speech enabled/disabled
    const handleToggle = () => {
        // If currently speaking, stop it
        if (isSpeaking) {
            if (speechRef.current) {
                speechRef.current.pause();
                speechRef.current.currentTime = 0;
                setIsSpeaking(false);
            }
            setSpeechEnabled(false);
        }
        // If not currently speaking, enable speech and play the most recent message
        else {
            setSpeechEnabled(true);
            // Find the most recent Sensei message
            if (messages.length > 0) {
                const senseiMessages = messages.filter(msg => msg.type === "Sensei");
                if (senseiMessages.length > 0) {
                    const lastSenseiMessage = senseiMessages[senseiMessages.length - 1];
                    // Update last played index to prevent duplicate plays
                    lastPlayedMessageIndex.current = messages.indexOf(lastSenseiMessage);
                    playMessage(lastSenseiMessage.text);
                }
            }
        }
    };

    return { handleToggle };
};

export default useSpeechControl;
